import { LOAD_STATE } from '@shared/axios/axios.constants';
import { createReducer, INITIAL_STATE } from '@shared/redux';
import {
    getCampaignsReportsConfig,
    getEditCampaignsReportsConfig,
} from './campaigns.api';
import {
    formatFieldValue,
    convertCampaignsToHash,
} from './campaigns.utils';
import { GetCampaignsReportsStatsParams, CampaignsState } from './campaigns.types';

export const GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_CALLED = '@campaigns/GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_CALLED';
export const GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_SUCCEEDED = '@campaigns/GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_SUCCEEDED';
export const GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_FAILED = '@campaigns/GET_CAMPAIGNS_CUSTOMERS_ID_REQUEST_FAILED';
export const GET_CAMPAIGNS_CUSTOMERS_ID_REQUEST_CANCELED = '@campaigns/GET_CAMPAIGNS_CUSTOMERS_ID_REQUEST_CANCELED';

export const EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_CALLED = '@campaigns/EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_CALLED';
export const EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_SUCCEEDED = '@campaigns/EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_SUCCEEDED';
export const EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_FAILED = '@campaigns/EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_FAILED';

export const HANDLE_GET_CAMPAIGNS_REPORT = '@dashboard/campaigns/HANDLE_GET_CAMPAIGNS_REPORT';

export const getCampaigns = state => state.campaigns.currentCampaigns;

export const handleGetCampaignsReport = () => ({
    type: HANDLE_GET_CAMPAIGNS_REPORT,
    payload: {},
});

export const getCampaignsReportsStats = ({
    customerType,
    customerId,
    startDate,
    endDate,
    isNewOverview,
    conversionType,
}: GetCampaignsReportsStatsParams) => ({
    type: GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_CALLED,
    actionTypes: {
        success: GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_SUCCEEDED,
        fail: GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_FAILED,
        canceled: GET_CAMPAIGNS_CUSTOMERS_ID_REQUEST_CANCELED,
    },
    config: getCampaignsReportsConfig({
        customerType,
        customerId,
        startDate,
        endDate,
        isNewOverview,
        conversionType,
    }),
});

export const editCampaignReportsStats = ({
    accountId,
    platform,
    campaignId,
    subLevelId,
    field,
    fieldValue,
    originalValue,
    budgetId,
}) => ({
    type: EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_CALLED,
    actionTypes: {
        success: EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_SUCCEEDED,
        fail: EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_FAILED,
    },
    config: getEditCampaignsReportsConfig({
        accountId,
        platform,
        campaignId,
        subLevelId,
        field,
        fieldValue: formatFieldValue(field, fieldValue),
        budgetId,
    }),
    payload: {
        accountId,
        platform,
        campaignId,
        subLevelId,
        field,
        fieldValue,
        originalValue,
    },
});

const editCampaign = (
    data, campaignId, field, fieldValue, loading,
) => {
    const campaignToEdit = data[campaignId];

    const editedCampaign = {
        ...campaignToEdit,
        loading,
        [field]: formatFieldValue(field, fieldValue),
    };

    return {
        ...data,
        [campaignId]: editedCampaign,
    };
};

const initialState: CampaignsState = {
    loadState: LOAD_STATE.LOADING,
    data: {},
    error: null,
    warning: null,
};

export const campaignsReducer = createReducer({
    [INITIAL_STATE]: initialState,
    [GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_CALLED]: state => ({
        ...state,
        loadState: LOAD_STATE.LOADING,
        data: {},
        error: null,
    }),
    [GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_SUCCEEDED]: (state, action) => ({
        ...state,
        loadState: LOAD_STATE.SUCCESS,
        data: convertCampaignsToHash({ report: action.payload.data }),
        error: null,
        warning: action.payload.data?.warning,
    }),
    [GET_CAMPAIGNS_CUSTOMER_ID_REQUEST_FAILED]: (state, action) => ({
        ...state,
        loadState: LOAD_STATE.FAIL,
        data: {},
        error: action.payload,
    }),
    [GET_CAMPAIGNS_CUSTOMERS_ID_REQUEST_CANCELED]: (state, action) => ({
        ...state,
        loadState: LOAD_STATE.LOADING,
        data: {},
        error: action.payload,
    }),
    [EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_CALLED]: (state, action) => ({
        ...state,
        data: editCampaign(
            state.data,
            action.payload.campaignId,
            action.payload.field,
            action.payload.fieldValue,
            true,
        ),
        error: null,
    }),
    [EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_SUCCEEDED]: (state, action) => ({
        ...state,
        data: editCampaign(
            state.data,
            action.requestAction.payload.campaignId,
            action.requestAction.payload.field,
            action.requestAction.payload.fieldValue,
            false,
        ),
        error: null,
    }),
    [EDIT_CAMPAIGN_CUSTOMER_ID_REQUEST_FAILED]: (state, action) => ({
        ...state,
        data: editCampaign(
            state.data,
            action.requestAction.payload.campaignId,
            action.requestAction.payload.field,
            formatFieldValue(
                action.requestAction.payload.field,
                action.requestAction.payload.originalValue,
            ),
            false,
        ),
        error: action.payload,
        warning: action.requestAction.payload.field,
    }),
});
